/**
 * Specialist gear arithmetic (04 §6): smoke grenades, night-vision goggles, wire cutters and the
 * suppressed pistol. Found, never bought - so it costs no RP - but it comes out of a limited
 * operation stock, rides on the ranger only (never in a pod) and weighs on him like anything
 * else. Pure. The weights are the simulation's own (`GEAR_WEIGHT`, src/sim/tuning), the numbers
 * that slow him on the ground - so the bar and the burden line cannot drift from play; `GameApi
 * .prices().weight` carries the same figures. Stock comes from `GameApi.stock`.
 */

import type { SpecialItem } from '../../../meta/types';
import { GEAR_WEIGHT } from '../../../sim/tuning';
import type { Gear } from '../../../sim/types';

export const GEAR_ITEMS: readonly SpecialItem[] = ['smoke', 'nvg', 'wire-cutters', 'suppressed-pistol', 'ghillie'];

/** How many of each the ranger has taken: 0-3 smoke, 0 or 1 of everything else. */
export type GearState = Record<SpecialItem, number>;
export type Stock = Partial<Record<SpecialItem, number>>;

/** A ranger carries at most three smoke grenades; the rest are one-of items. */
export const GEAR_MAX: Readonly<Record<SpecialItem, number>> = {
  smoke: 3,
  nvg: 1,
  'wire-cutters': 1,
  'suppressed-pistol': 1,
  ghillie: 1,
};

/** The suppressed pistol is issued with exactly this many rounds. */
export const PISTOL_ROUNDS = 14;

/** Carry units one of `item` adds to the ranger. */
export function unitWeight(item: SpecialItem): number {
  switch (item) {
    case 'smoke':
      return GEAR_WEIGHT.smoke;
    case 'nvg':
      return GEAR_WEIGHT.nvg;
    case 'wire-cutters':
      return GEAR_WEIGHT.wireCutters;
    case 'suppressed-pistol':
      return GEAR_WEIGHT.pistol;
    case 'ghillie':
      return GEAR_WEIGHT.ghillie;
  }
}

export function emptyGear(): GearState {
  return { smoke: 0, nvg: 0, 'wire-cutters': 0, 'suppressed-pistol': 0, ghillie: 0 };
}

export function gearWeight(gear: GearState | undefined): number {
  if (gear === undefined) return 0;
  let total = 0;
  for (const item of GEAR_ITEMS) total += gear[item] * unitWeight(item);
  return total;
}

export function inStock(stock: Stock, item: SpecialItem): number {
  return Math.max(0, Math.floor(stock[item] ?? 0));
}

/** True when the operation holds any specialist gear at all: otherwise the block is not shown. */
export function anyStock(stock: Stock): boolean {
  return GEAR_ITEMS.some((item) => inStock(stock, item) > 0);
}

export type GearBlock = 'stock' | 'max' | 'weight' | null;

/**
 * Why one more of `item` cannot be taken, or null. `carriedWeight` is everything on the ranger
 * now, gear included; `carryCap` is his limit (`PriceList.carryCap`).
 */
export function gearBlock(
  gear: GearState,
  item: SpecialItem,
  stock: Stock,
  carriedWeight: number,
  carryCap: number,
): GearBlock {
  if (gear[item] >= GEAR_MAX[item]) return 'max';
  if (gear[item] >= inStock(stock, item)) return 'stock';
  return carriedWeight + unitWeight(item) > carryCap ? 'weight' : null;
}

export function gearBlockText(block: GearBlock): string {
  if (block === 'stock') return 'NONE LEFT IN STOCK';
  if (block === 'weight') return 'OVER WEIGHT';
  return block === 'max' ? 'THAT IS ALL HE CAN CARRY' : '';
}

/** A new state with `delta` applied; the same state when the change is not allowed. */
export function adjustGear(
  gear: GearState,
  item: SpecialItem,
  delta: 1 | -1,
  stock: Stock,
  carriedWeight: number,
  carryCap: number,
): GearState {
  if (delta === 1 && gearBlock(gear, item, stock, carriedWeight, carryCap) !== null) return gear;
  if (delta === -1 && gear[item] <= 0) return gear;
  return { ...gear, [item]: gear[item] + delta };
}

/**
 * `Loadout.gear`: only what was taken, so nothing reads as "zero smoke"; undefined when he takes
 * nothing. The pistol always comes with its fourteen rounds.
 */
export function toGear(gear: GearState | undefined): Partial<Gear> | undefined {
  if (gear === undefined) return undefined;
  const out: Partial<Gear> = {};
  if (gear.smoke > 0) out.smoke = gear.smoke;
  if (gear['suppressed-pistol'] > 0) out.pistolRounds = PISTOL_ROUNDS;
  if (gear.nvg > 0) out.nvg = true;
  if (gear['wire-cutters'] > 0) out.wireCutters = true;
  if (gear.ghillie > 0) out.ghillie = true;
  return Object.keys(out).length > 0 ? out : undefined;
}
